import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  ForecastLine,
  ImportChoices,
  ImportCommitResult,
  ImportPreview,
  LineCreateBody,
  LineEditBody,
  MyCustomer,
  PricelistProduct,
  RollupFilters,
  RollupRow,
} from './types'

const ROOT = '/api/forecasts'

/** Scoped rep's assigned customers, for the entry-page customer picker.
 *  400s for unscoped roles by design (see route docstring) — callers only
 *  invoke this for scoped roles (outside_sales_rep / inside_sales_rep). */
export function useMyCustomers(enabled: boolean) {
  return useQuery({
    queryKey: ['forecasts', 'my-customers'],
    queryFn: () => jsonFetch<MyCustomer[]>(`${ROOT}/my-customers`),
    enabled,
    staleTime: 60_000,
  })
}

export function useForecastLines(customerId: number | null) {
  return useQuery({
    queryKey: ['forecasts', 'lines', customerId],
    queryFn: () =>
      jsonFetch<ForecastLine[]>(`${ROOT}/lines?customer_id=${customerId}`),
    enabled: customerId !== null,
    staleTime: 15_000,
  })
}

/** The customer's FULL pricelist product list (limit=0 — Odoo's own
 *  "unbounded" convention), for auto-populating one draft row per
 *  product when the entry page's customer changes. Distinct from the
 *  typeahead search inside `PricelistAwareProductPicker`, which hits the
 *  same endpoint but with the small default `limit` for interactive
 *  search-as-you-type. */
export function usePricelistProducts(customerId: number | null) {
  return useQuery({
    queryKey: ['forecasts', 'pricelist-products', customerId],
    queryFn: () =>
      jsonFetch<{ products: PricelistProduct[] }>(
        `${ROOT}/customers/${customerId}/pricelist-products?limit=0`,
      ).then((r) => r.products),
    enabled: customerId !== null,
    staleTime: 60_000,
  })
}

export function useCreateForecastLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: LineCreateBody) =>
      jsonFetch<ForecastLine>(`${ROOT}/lines`, { method: 'POST', body }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['forecasts', 'lines', data.customer_id] })
    },
  })
}

export interface UpdateLineVars {
  lineId: number
  customerId: number
  edits: LineEditBody
}

export function useUpdateForecastLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: UpdateLineVars) =>
      jsonFetch<ForecastLine>(`${ROOT}/lines/${vars.lineId}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['forecasts', 'lines', vars.customerId] })
    },
  })
}

export interface DeleteLineVars {
  lineId: number
  customerId: number
}

export function useDeleteForecastLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: DeleteLineVars) =>
      jsonFetch<null>(`${ROOT}/lines/${vars.lineId}`, { method: 'DELETE' }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['forecasts', 'lines', vars.customerId] })
    },
  })
}

function rollupParams(filters: RollupFilters): string {
  const params = new URLSearchParams({
    period_from: filters.periodFrom,
    period_to: filters.periodTo,
  })
  if (filters.customerId != null) params.set('customer_id', String(filters.customerId))
  if (filters.productId != null) params.set('product_id', String(filters.productId))
  return params.toString()
}

export function useForecastRollup(filters: RollupFilters) {
  return useQuery({
    queryKey: ['forecasts', 'rollup', filters],
    queryFn: () => jsonFetch<RollupRow[]>(`${ROOT}/rollup?${rollupParams(filters)}`),
    staleTime: 15_000,
  })
}

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

/** Direct-link URL for the CSV export button — not a react-query hook,
 *  since it's a browser-navigated download, not fetched-and-rendered data.
 *  Mirrors customer-pricelists' PricelistDetail.tsx export-link pattern. */
export function rollupExportCsvUrl(filters: RollupFilters): string {
  return `${API_BASE}${ROOT}/rollup/export.csv?${rollupParams(filters)}`
}

function importForm(c: ImportChoices): FormData {
  const fd = new FormData()
  fd.append('file', c.file)
  fd.append('customer_id', String(c.customerId))
  if (c.dateOrder) fd.append('date_order', c.dateOrder)
  fd.append('overrides', JSON.stringify(c.overrides))
  fd.append('skip', JSON.stringify(c.skip))
  return fd
}

/** Parse + match a forecast spreadsheet. Writes nothing; re-run with the
 *  user's choices (date order, product picks, skipped rows). */
export function useForecastImportPreview() {
  return useMutation({
    mutationFn: (c: ImportChoices) =>
      jsonFetch<ImportPreview>(`${ROOT}/import/preview`, { method: 'POST', rawBody: importForm(c) }),
  })
}

/** Same file + choices; the server rebuilds the plan, replaces the
 *  overlapping lines and inserts the new ones in one transaction. */
export function useForecastImportCommit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (c: ImportChoices) =>
      jsonFetch<ImportCommitResult>(`${ROOT}/import/commit`, { method: 'POST', rawBody: importForm(c) }),
    onSuccess: (_data, c) => {
      void qc.invalidateQueries({ queryKey: ['forecasts', 'lines', c.customerId] })
      void qc.invalidateQueries({ queryKey: ['forecasts', 'rollup'] })
    },
  })
}
